/**
 * @file index.tsx
 *
 * @fileoverview Generates a box element. A div with some box styling properties
 * that come from the theme.
 */

import { cva } from "class-variance-authority";
import * as React from "react";
import { BorderProps, generateBorderClasses } from "../../../theme/border";
import {
  ColorsProps,
  excludeColorProps,
  generateColorClasses,
} from "../../../theme/colors";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../theme/spacing";
import styles from "./box.module.scss";

const box = cva([styles.box], {
  variants: {
    paddingClean: {
      true: styles.boxPaddingClean,
    },
    marginClean: {
      true: styles.boxMarginClean,
    },
  },
  defaultVariants: {
    paddingClean: false,
    marginClean: false,
  },
});

export type BoxProps = ColorsProps &
  SpaceProps &
  BorderProps & {
    /**
     * The content that goes inside the box component.
     */
    children: React.ReactNode;
    /**
     * The title to be displaye in the top of the box.
     *
     * @default ""
     */
    label?: string | React.ReactNode;
    /**
     * If the box should have a clean padding (no padding).
     *
     * @default false
     */
    paddingClean?: boolean;
    /**
     * If the box should have a clean margin (no margin).
     *
     * @default false
     */
    marginClean?: boolean;
    /**
     * The className.
     * This prop gets appended to the already existing classes.
     */
    className?: string;
    /**
     * The box shadow styling.
     */
    boxShadow?: string;
    /**
     * The border radius styling.
     */
    borderRadius?: string;
    /**
     * The background color override.
     * This prop is used as a inline style in the component.
     */
    backgroundColorOverride?: string;
  };

/**
 * This component renders a box with some content inside. It has specific styling passed vai the theme object. It can be used in a
 * variety of situations but always should be used agains the background so that it contrasts with the background color.
 */
export default function Box({
  children,
  label,
  marginClean = false,
  paddingClean = false,
  borderTopStyle,
  borderTopWidth,
  borderBottomStyle,
  borderBottomWidth,
  borderRightStyle,
  borderRightWidth,
  borderLeftStyle,
  borderLeftWidth,
  boxShadow,
  borderRadius,
  backgroundColorOverride,
  className = "",
  ...props
}: BoxProps) {
  return (
    <div
      className={`
      vcl-box
      ${box({ marginClean, paddingClean })}
      ${generateBorderClasses(props, styles)}
      ${generateMarginClasses(props, styles)}
      ${generatePaddingClasses(props, styles)}
      ${generateColorClasses(props, styles)}
      ${className}
      `.trim()}
      data-testid="box"
      style={{
        borderTopStyle,
        borderTopWidth,
        borderBottomStyle,
        borderBottomWidth,
        borderRightStyle,
        borderRightWidth,
        borderLeftStyle,
        borderLeftWidth,
        ...(backgroundColorOverride && {
          backgroundColor: backgroundColorOverride,
        }),
        ...(boxShadow && { boxShadow }),
        ...(borderRadius && { borderRadius }),
      }}
      {...excludeColorProps(excludeSpacingProps(props))}
    >
      {label && <span className={styles.boxTitle}>{label}</span>}
      {children}
    </div>
  );
}

export { Box };
